import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { ArrowLeft, ArrowRight, ShieldCheck, Check, Eye, EyeOff, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { ThemeToggle } from '../components/ThemeToggle';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CODE_LENGTH = 6;
const RESEND_COOLDOWN = 30;

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [identifier, setIdentifier] = useState('');
  const [delivery, setDelivery] = useState('');
  const [maskedDest, setMaskedDest] = useState('');
  const [code, setCode] = useState(Array(CODE_LENGTH).fill(''));
  const [resetToken, setResetToken] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendTimer, setResendTimer] = useState(0);
  const [resending, setResending] = useState(false);
  const inputRefs = useRef([]);

  useEffect(() => {
    if (resendTimer <= 0) return;
    const t = setInterval(() => setResendTimer(v => v - 1), 1000);
    return () => clearInterval(t);
  }, [resendTimer]);

  const handleRequestCode = async (e) => {
    e.preventDefault();
    if (!identifier.trim()) return;
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.post(`${API}/unified-auth/forgot-password`, { identifier: identifier.trim() });
      setDelivery(data.delivery || 'email');
      setMaskedDest(data.masked_destination || '');
      setStep(2);
      setResendTimer(RESEND_COOLDOWN);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    }
    setLoading(false);
  };

  const handleCodeChange = (index, value) => {
    const digit = value.replace(/\D/g, '').slice(-1);
    const newCode = [...code];
    newCode[index] = digit;
    setCode(newCode);
    setError('');
    if (digit && index < CODE_LENGTH - 1) inputRefs.current[index + 1]?.focus();
  };

  const handleCodeKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) inputRefs.current[index - 1]?.focus();
  };

  const handleCodePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, CODE_LENGTH);
    if (pasted.length > 0) {
      const newCode = Array(CODE_LENGTH).fill('');
      pasted.split('').forEach((d, i) => { newCode[i] = d; });
      setCode(newCode);
      inputRefs.current[Math.min(pasted.length, CODE_LENGTH) - 1]?.focus();
    }
  };

  const fullCode = code.join('');
  const codeComplete = fullCode.length === CODE_LENGTH;

  const handleVerifyCode = async () => {
    if (!codeComplete) return;
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.post(`${API}/unified-auth/forgot-password/verify`, {
        identifier: identifier.trim(),
        code: fullCode,
      });
      setResetToken(data.reset_token);
      setStep(3);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid or expired code');
      setCode(Array(CODE_LENGTH).fill(''));
      inputRefs.current[0]?.focus();
    }
    setLoading(false);
  };

  const handleResend = async () => {
    if (resendTimer > 0 || resending) return;
    setResending(true);
    try {
      await axios.post(`${API}/unified-auth/forgot-password`, { identifier: identifier.trim() });
      toast.success('New code sent');
      setResendTimer(RESEND_COOLDOWN);
    } catch {
      toast.error('Could not resend code');
    }
    setResending(false);
  };

  const handleSetPassword = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) return setError('Password must be at least 8 characters');
    if (password !== confirmPw) return setError('Passwords do not match');
    setLoading(true);
    try {
      await axios.post(`${API}/unified-auth/forgot-password/reset`, {
        reset_token: resetToken,
        new_password: password,
      });
      setStep(4);
    } catch (err) {
      setError(err.response?.data?.detail || 'Reset failed. Please try again.');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4" data-testid="forgot-password-page">
      <div className="fixed top-4 right-4 z-50"><ThemeToggle compact /></div>
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-4">
            <div className="w-9 h-9 rounded-lg overflow-hidden shrink-0">
              <img src="/assets/agent9-icon.png" alt="Agent9" className="w-full h-full object-contain" />
            </div>
            <span className="text-xl font-bold text-foreground tracking-tight">Agent9</span>
          </Link>
        </div>

        {step === 1 && (
          <Card className="bg-card border-border backdrop-blur-sm">
            <CardContent className="p-6">
              <h1 className="text-xl font-bold text-foreground text-center" data-testid="forgot-heading">Reset your password</h1>
              <p className="text-muted-foreground text-sm text-center mt-1 mb-6">Enter your email or phone number</p>

              <form onSubmit={handleRequestCode} className="space-y-4">
                {error && (
                  <div className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-lg px-3 py-2" data-testid="forgot-error">{error}</div>
                )}
                <div className="space-y-1.5">
                  <Label className="text-foreground text-sm">Email or phone number</Label>
                  <Input value={identifier} onChange={(e) => setIdentifier(e.target.value)}
                    placeholder="you@company.com or +1 555-123-4567" required
                    className="bg-muted border-border text-foreground placeholder:text-muted-foreground h-11"
                    data-testid="forgot-identifier-input" />
                </div>

                <Button type="submit" disabled={loading}
                  className="w-full h-11 bg-primary hover:bg-primary/90 text-primary-foreground font-medium" data-testid="forgot-submit-btn">
                  {loading ? <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary-foreground border-t-transparent" /> : <>Send code <ArrowRight size={16} className="ml-1" /></>}
                </Button>
              </form>

              <p className="text-center text-muted-foreground text-sm mt-5">
                <Link to="/login" className="text-primary hover:text-primary/80 inline-flex items-center gap-1"><ArrowLeft size={14} /> Back to login</Link>
              </p>
            </CardContent>
          </Card>
        )}

        {step === 2 && (
          <Card className="bg-card border-border backdrop-blur-sm">
            <CardContent className="p-6 text-center">
              <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <ShieldCheck size={28} className="text-primary" />
              </div>
              <h1 className="text-xl font-bold text-foreground" data-testid="verify-code-heading">Check your {delivery === 'sms' ? 'phone' : 'email'}</h1>
              <p className="text-muted-foreground text-sm mt-2">
                We sent a 6-digit code to <span className="text-foreground font-medium">{maskedDest}</span>
              </p>

              {error && (
                <div className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-lg px-3 py-2 mt-4" data-testid="verify-code-error">{error}</div>
              )}

              <div className="flex justify-center gap-2.5 mt-6" onPaste={handleCodePaste}>
                {code.map((digit, i) => (
                  <input key={i} ref={el => inputRefs.current[i] = el}
                    type="text" inputMode="numeric" maxLength={1} value={digit}
                    onChange={(e) => handleCodeChange(i, e.target.value)}
                    onKeyDown={(e) => handleCodeKeyDown(i, e)}
                    className="w-12 h-14 text-center text-xl font-bold bg-muted border border-border rounded-lg text-foreground focus:border-primary focus:ring-1 focus:ring-primary/30 outline-none transition-all"
                    data-testid={`forgot-otp-input-${i}`} />
                ))}
              </div>

              <Button onClick={handleVerifyCode} disabled={!codeComplete || loading}
                className="w-full h-11 bg-primary hover:bg-primary/90 text-primary-foreground font-medium mt-6" data-testid="forgot-verify-btn">
                {loading ? <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary-foreground border-t-transparent" /> : 'Verify code'}
              </Button>

              <div className="mt-4">
                {resendTimer > 0 ? (
                  <p className="text-muted-foreground text-sm">Resend code in <span className="text-foreground font-medium">{resendTimer}s</span></p>
                ) : (
                  <button onClick={handleResend} disabled={resending}
                    className="inline-flex items-center gap-1.5 text-sm text-primary hover:text-primary/80 transition-colors" data-testid="forgot-resend-btn">
                    <RotateCcw size={14} /> {resending ? 'Sending...' : 'Resend code'}
                  </button>
                )}
              </div>

              <button onClick={() => { setStep(1); setCode(Array(CODE_LENGTH).fill('')); setError(''); }}
                className="text-muted-foreground hover:text-foreground text-sm mt-4 inline-flex items-center gap-1 transition-colors" data-testid="forgot-back-btn">
                <ArrowLeft size={14} /> Try a different email or phone
              </button>
            </CardContent>
          </Card>
        )}

        {step === 3 && (
          <Card className="bg-card border-border backdrop-blur-sm">
            <CardContent className="p-6">
              <h1 className="text-xl font-bold text-foreground text-center" data-testid="new-password-heading">Set a new password</h1>
              <p className="text-muted-foreground text-sm text-center mt-1 mb-6">Choose a strong password for your account</p>

              <form onSubmit={handleSetPassword} className="space-y-4">
                {error && (
                  <div className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-lg px-3 py-2" data-testid="new-password-error">{error}</div>
                )}
                <div className="space-y-1.5">
                  <Label className="text-foreground text-sm">New password</Label>
                  <div className="relative">
                    <Input type={showPw ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)}
                      placeholder="Min. 8 characters" required minLength={8}
                      className="bg-muted border-border text-foreground placeholder:text-muted-foreground h-11 pr-10"
                      data-testid="new-password-input" />
                    <button type="button" onClick={() => setShowPw(!showPw)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                      {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-foreground text-sm">Confirm password</Label>
                  <Input type={showPw ? 'text' : 'password'} value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)}
                    placeholder="Re-enter password" required
                    className="bg-muted border-border text-foreground placeholder:text-muted-foreground h-11"
                    data-testid="confirm-password-input" />
                </div>

                <Button type="submit" disabled={loading}
                  className="w-full h-11 bg-primary hover:bg-primary/90 text-primary-foreground font-medium" data-testid="reset-password-btn">
                  {loading ? <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary-foreground border-t-transparent" /> : 'Reset password'}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {step === 4 && (
          <Card className="bg-card border-border backdrop-blur-sm">
            <CardContent className="p-6 text-center" data-testid="reset-success">
              <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <Check size={28} className="text-primary" />
              </div>
              <h1 className="text-xl font-bold text-foreground">Password updated</h1>
              <p className="text-muted-foreground text-sm mt-2">Your password has been reset. You can now log in.</p>
              <Button onClick={() => navigate('/login')}
                className="w-full h-11 bg-primary hover:bg-primary/90 text-primary-foreground font-medium mt-6" data-testid="go-to-login-btn">
                Go to login <ArrowRight size={16} className="ml-1" />
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
